import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FileText, Upload, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { authFetch } from "@/lib/auth";
import { format, parseISO } from "date-fns";
import type { Document, DocType, Correspondent } from "@shared/types";
export function DocumentsPage() {
  const [docs, setDocs] = useState<Document[]>([]);
  const [types, setTypes] = useState<DocType[]>([]);
  const [correspondents, setCorrespondents] = useState<Correspondent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Taxonomy lives under /api/docs/taxonomy/* per the 2026-05-02
        // audit fix. Calling /api/taxonomy/* 404s and silently empties
        // the type and correspondent dropdowns.
        const [dData, tData, cData] = await Promise.all([
          authFetch<{ items: Document[] }>("/api/docs"),
          authFetch<{ items: DocType[] }>("/api/docs/taxonomy/types"),
          authFetch<{ items: Correspondent[] }>("/api/docs/taxonomy/correspondents"),
        ]);
        if (!cancelled) {
          setDocs(dData.items ?? []);
          setTypes(tData.items ?? []);
          setCorrespondents(cData.items ?? []);
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!cancelled) setError(msg);
        console.error("[Documents]", msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);
  const typeMap = useMemo(() => new Map(types.map((t) => [t.id, t.name])), [types]);
  const corrMap = useMemo(() => new Map(correspondents.map((c) => [c.id, c.name])), [correspondents]);
  // Read fields with legacy fallbacks so the card still renders if the
  // wire shape lags the canonical column names.
  const typeIdOf = (d: Document): string => (d.document_type_id ?? d.type_id ?? "") as string;
  const dateOf = (d: Document): string | null => (d.date_on_document ?? d.date_received ?? null) as string | null;
  const filtered = useMemo(() => {
    return docs.filter((d) => {
      const ms = (d.title ?? "").toLowerCase().includes(search.toLowerCase());
      const mt = typeFilter === "all" || typeIdOf(d) === typeFilter;
      const mst = statusFilter === "all" || d.processing_status === statusFilter;
      return ms && mt && mst;
    });
  }, [docs, search, typeFilter, statusFilter]);
  return (
    <>
      <PageHeader title="Documents" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Documents" }]}>
        <Link to="/documents/upload">
          <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
            <Upload className="h-4 w-4 mr-2" /> Upload Document
          </Button>
        </Link>
      </PageHeader>
      {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search documents..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="extracting">Extracting</SelectItem>
            <SelectItem value="classifying">Classifying</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
            <SelectItem value="done">Done</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <CardGridSkeleton count={4} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={FileText} title="No documents found" description={search || typeFilter !== "all" || statusFilter !== "all" ? "Try adjusting your filters." : "Upload your first document to get started."} actionLabel={!search && typeFilter === "all" && statusFilter === "all" ? "Upload Document" : undefined} onAction={undefined} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((doc) => {
            const tid = typeIdOf(doc);
            const cid = doc.correspondent_id ?? "";
            const date = dateOf(doc);
            return (
              <Link key={doc.id} to={`/documents/${doc.id}`}>
                <Card className="p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer group">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-5 w-5 text-ranch-gold shrink-0 group-hover:scale-110 transition-transform" />
                      <h3 className="font-semibold text-foreground truncate">{doc.title}</h3>
                    </div>
                    <StatusBadge status={doc.processing_status} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {typeMap.get(tid) || "Unknown type"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {corrMap.get(cid) || "Unknown correspondent"}
                  </p>
                  <p className="text-2xs text-muted-foreground mt-2">
                    {date ? format(parseISO(date + "T00:00:00"), "MMM d, yyyy") : "—"}
                  </p>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
